import React from 'react';
import { FieldTemplateProps } from "@rjsf/utils";
import { Form as AntForm } from 'antd';
import { isArrayItemId } from './ids';

/**
 * One field of a schema-generated form: its name on the left, its control on
 * the right, on one line.
 *
 * An array is the exception. Its control is a stack of rows with an add button
 * under them, so its name goes above it — beside a six-row list, a label
 * hanging off the first row reads as belonging to that row.
 */
const CustomFieldTemplate = (props: FieldTemplateProps) => {
    const { label, id, required, children, description, uiSchema, schema, errors, rawHelp, rawErrors } = props;

    const isArray = schema.type === 'array';
    const isObject = schema.type === 'object';

    /* Objects label themselves through the title template, and an array item's
       label is its position, which the array template shows. */
    const showLabel = isArray
        ? !isArrayItemId(id)
        : !isObject && !isArrayItemId(id) && !uiSchema;

    /* `description` is the *rendered* DescriptionField, not the text — it is a
       React element whether or not the schema said anything, so testing it is
       always true and every field got an empty <div> under its control. That
       div was 5px tall, which was enough to push the row past the height of
       its input and take the label out of line with it. The schema is what
       actually knows whether there is a description.

       An object's is left to the object template, which puts it under the
       heading rather than after the whole nested block. */
    const showDescription = Boolean(schema.description) && !isObject;

    /* Objects and arrays are blocks: they get the full width and no label
       column, so the group's own heading sits flush with the fields under it
       rather than being pushed in by an empty gutter. */
    const isBlock = isObject || isArray || isArrayItemId(id) || Boolean(uiSchema);

    return (
        <AntForm.Item
            className={`sb-field${isBlock ? ' sb-field--block' : ''}`}
            label={showLabel ? label : undefined}
            htmlFor={id}
            required={required}
            colon={false}
            labelAlign="left"
            /* a fixed label track rather than a 24-column split: every control
               down the form then starts at the same x, whatever its name is,
               and each takes all the width left over */
            labelCol={isBlock ? { flex: '1 1 100%' } : { flex: '0 0 var(--sb-label-w)' }}
            wrapperCol={isBlock ? { flex: '1 1 100%' } : { flex: '1 1 0' }}
            help={(!!rawHelp) || (rawErrors?.length ? errors : undefined)}
            validateStatus={rawErrors?.length ? 'error' : undefined}
        >
            {children}
            {showDescription && <div className="sb-field__desc">{description}</div>}
        </AntForm.Item>
    );
};

export default CustomFieldTemplate;
